
import React from "react";
import { Card, CardHeader, CardContent, CardDescription, CardTitle, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CheckCircle } from "lucide-react";

interface AvailableAssistantsStageProps {
  aiCandidates: Array<{
    id: number | string;
    name: string;
    avatar: string;
    role: string;
    description: string;
    skills: string[];
    benefits: string;
  }>;
  selectedCandidate: number | null;
  onSelectCandidate: (id: number) => void;
}

const AvailableAssistantsStage: React.FC<AvailableAssistantsStageProps> = ({
  aiCandidates,
  selectedCandidate,
  onSelectCandidate,
}) => {
  return (
    <div className="space-y-6 animate-fade-in">
      <div className="space-y-4">
        <h3 className="text-2xl font-bold">Choose Your AI Assistant</h3>
        <p className="text-muted-foreground">
          Select the type of AI assistant that best fits your business needs.
        </p>
        
        {/* AI Assistant Selection Cards */}
        <div className="grid md:grid-cols-2 gap-6 mt-6">
          {aiCandidates.map((candidate) => (
            <Card 
              key={candidate.id}
              className={`hover:shadow-md transition-all cursor-pointer ${
                selectedCandidate === Number(candidate.id) ? 'ring-2 ring-primary' : ''
              }`}
              onClick={() => onSelectCandidate(Number(candidate.id))}
            >
              <CardHeader className="pb-2">
                <div className="flex items-center gap-3">
                  <div className="text-4xl">{candidate.avatar}</div>
                  <div>
                    <CardTitle className="text-xl">{candidate.name}</CardTitle>
                    <CardDescription className="opacity-75">
                      {candidate.role === "marketing" ? "Available Now" : "Coming Soon"}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm">{candidate.description}</p>
                <div>
                  <span className="text-sm font-medium">Key Skills:</span>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {candidate.skills.map((skill) => (
                      <Badge key={skill} variant="secondary" className="text-xs mb-1">
                        {skill}
                      </Badge>
                    ))}
                  </div>
                </div>
                <div className="bg-primary/5 p-3 rounded-md mt-2">
                  <span className="text-sm font-medium">Benefit:</span>
                  <p className="text-sm mt-1 text-muted-foreground">{candidate.benefits}</p>
                </div>
              </CardContent>
              <CardFooter className="pt-0 justify-center">
                {selectedCandidate === Number(candidate.id) ? (
                  <div className="flex items-center gap-2 text-primary">
                    <CheckCircle className="h-4 w-4" />
                    <span className="text-sm font-medium">Selected</span>
                  </div>
                ) : (
                  <Button 
                    variant="outline" 
                    onClick={() => onSelectCandidate(Number(candidate.id))}
                    disabled={candidate.role !== "marketing"}
                    className="w-full"
                  >
                    {candidate.role === "marketing" ? "Select" : "Coming Soon"}
                  </Button>
                )}
              </CardFooter>
            </Card>
          ))}
        </div>

        <div className="text-center mt-6">
          <p className="text-sm text-muted-foreground">
            Note: Currently only Marketing Assistant is available. More assistant types will be available soon!
          </p>
        </div>
      </div>
    </div>
  );
};

export default AvailableAssistantsStage;
